import React, { useEffect, useState } from 'react';
import { Asset } from 'contentful';
import { Document } from '@contentful/rich-text-types';

import { Breadcrumbs } from '../../../Breadcrumbs';
import { TagsList } from './TagsList';
import { PostInfos } from './PostInfos';
import {
  ILearningCenterTag,
  ISeoMetadataFields
} from '../../../../../types/contentful';
import { SocialShare } from './SocialShare';

interface BlogArticleHeaderProps {
  title: string;
  thumbnail?: Asset;
  summary?: Document;
  category?: string;
  tagsList?: ILearningCenterTag[];
  publishDate: string;
  difficulty: string;
  sysId?: string;
  seoFields?: ISeoMetadataFields;
}

export function ArticleHeader({
  title,
  thumbnail,
  category,
  tagsList,
  publishDate,
  seoFields
}: BlogArticleHeaderProps) {
  const [completeTagsList, setCompleteTagsList] = useState<string[]>([]);

  useEffect(() => {
    const newTagsList = [];

    if (tagsList) {
      newTagsList.push(...tagsList?.map((tag) => tag.fields.name));
    }

    if (category) {
      newTagsList.unshift(category);
    }

    // this is required to avoid an infinite rerender loop
    if (newTagsList.toString() !== completeTagsList.toString()) {
      setCompleteTagsList(newTagsList);
    }
  }, [category, tagsList]);

  return (
    <div
      className="relative z-[1] flex w-full flex-col items-center justify-center desktop:mx-0"
      data-testid="blog-article-header"
    >
      {/** Breadcrumbs */}
      <div className="mb-4 mt-4 flex w-full items-center gap-[6px] px-6 desktop:mb-6">
        <Breadcrumbs
          listItemProps={{
            className:
              "before:mx-2 before:content-['/'] first:before:content-none inline before:typography-button-text-links-body-3 before:font-[350]"
          }}
          linkProps={{
            className:
              'typography-button-text-links-body-3 text-xs font-[350] hover:underline',
            style: {
              textDecorationStyle: 'solid',
              textDecorationThickness: '.5%',
              textUnderlineOffset: '32%',
              textUnderlinePosition: 'from-font'
            }
          }}
          className="order-1 desktop:order-none"
        />
      </div>

      <div className="tablet:block-container mx-4 mb-7 flex max-w-[1220px] flex-col items-center justify-center gap-4 self-stretch rounded-2xl bg-white p-4 tablet:mx-12 tablet:mb-0 tablet:flex-row tablet:gap-6 tablet:self-auto tablet:p-6 desktop:mx-12 desktop:items-center">
        {thumbnail && (
          <div className="hidden items-center justify-center tablet:flex tablet:aspect-[300/225] tablet:!max-w-[300px] desktop:aspect-[424/318] desktop:!max-w-[424px]">
            {/** Desktop Image */}
            <img
              className="h-full w-full rounded-lg object-cover"
              src={thumbnail?.fields.file.url}
              alt={thumbnail?.fields.title}
              data-testid="blog-hero-image"
            />
          </div>
        )}
        <div className="flex flex-grow flex-col items-start justify-center gap-3">
          <TagsList tags={completeTagsList} />
          <h1 className="w-full font-glRepro text-[28px] leading-8 tablet:text-[32px] tablet:leading-[125%] desktop:text-[48px] desktop:leading-[110%]">
            {title}
          </h1>
          <PostInfos publishDate={publishDate} />
          {thumbnail && (
            <div className="flex aspect-[323/272] w-full items-center justify-center tablet:hidden">
              {/** Mobile Image */}
              <img
                className="h-full w-full rounded-lg object-cover"
                src={thumbnail?.fields.file.url}
                alt={thumbnail?.fields.title}
                data-testid="blog-hero-image-mobile"
              />
            </div>
          )}
          <div className="block desktop:hidden">
            <SocialShare
              {...seoFields}
              socialOptions={'all'}
              inline
              hideShareCopy
            />
          </div>
        </div>
      </div>
    </div>
  );
}
